/**
 * PetCardList — the per-pet card editor (type-conditional fields, validation hints, add/remove).
 * Extracted verbatim from Step7PetDetails so the lease wizard and the amendment flow share one form.
 */

import type { PetDetail, PetType } from '../../types/lease-generation';
import { RequiredMark } from '../../pages/lease-wizard/RequiredMark';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { nativeSelectClass } from './form-classes';
import { Switch } from '../ui/switch';
import { BLANK_ANIMAL, petHasError } from './pet-validation';

export interface PetCardListProps {
  animals: PetDetail[];
  addAnimal: (animal: PetDetail) => void;
  updateAnimal: (index: number, update: Partial<PetDetail>) => void;
  removeAnimal: (index: number) => void;
  /** Show the "Review" chip on each card (wizard: pets pre-filled from the application). */
  prefilled?: boolean;
}

export function PetCardList({ animals, addAnimal, updateAnimal, removeAnimal, prefilled }: PetCardListProps) {
  const labelClass = 'block mb-1 text-sm font-medium text-foreground';

  return (
      <div>
        <div className="mb-3">
          <h4 className="text-sm font-semibold text-foreground mb-1">Pets on the Lease</h4>
          <p className="text-xs text-muted-foreground">
            List each pet individually. Each pet will appear in the signed pet addendum.
          </p>
        </div>

        {animals.length === 0 && (
          <div className="py-6 text-center bg-accent/50 rounded-lg border border-dashed border-border mb-4">
            <p className="text-sm text-muted-foreground">No pets added yet.</p>
          </div>
        )}

        {animals.map((animal, index) => (
          <div
            key={index}
            data-testid={`pet-card-${index}`}
            className="border border-border rounded-lg p-4 mb-3 bg-card"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-semibold text-foreground">
                  Pet {index + 1}{animal.name ? `: ${animal.name}` : ''}
                </h4>
                {prefilled && (
                  <span
                    data-testid="pet-prefill-review-chip"
                    className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground"
                    title="Pre-filled from application — please review and complete"
                  >
                    Review
                  </span>
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                data-testid={`remove-pet-${index}`}
                onClick={() => removeAnimal(index)}
                aria-label={`Remove pet ${index + 1}${animal.name ? ': ' + animal.name : ''}`}
                className="min-h-[44px] text-destructive border-destructive/30 hover:bg-destructive/5"
              >
                Remove
              </Button>
            </div>
            <div className="flex flex-col gap-3">
              {(() => {
                const animalHasError = petHasError(animal);
                const errorId = animalHasError ? `pet-error-${index}` : undefined;
                return (
                  <>
                    {/* Type selector — first field on the card */}
                    <div>
                      <label htmlFor={`pet-${index}-type`} className={labelClass}>
                        Type <RequiredMark />
                      </label>
                      <select
                        id={`pet-${index}-type`}
                        data-testid={`pet-${index}-type`}
                        value={animal.type}
                        onChange={e => {
                          const newType = e.target.value as PetType;
                          // Clear type-specific fields on type change; preserve name and color.
                          // Fields set to undefined are omitted by JSON.stringify — intentional.
                          updateAnimal(index, {
                            type: newType,
                            breed: undefined,
                            sex: undefined,
                            weight: undefined,
                            spayedNeutered: undefined,
                            vaccinationsCurrent: undefined,
                            species: undefined,
                            cageDescription: undefined,
                            enclosureDescription: undefined,
                            venomous: undefined,
                            tankSizeGallons: undefined,
                          });
                        }}
                        className={nativeSelectClass}
                        required
                        aria-required="true"
                      >
                        <option value="dog">Dog</option>
                        <option value="cat">Cat</option>
                        <option value="bird">Bird</option>
                        <option value="reptile">Reptile</option>
                        <option value="fish">Fish</option>
                        <option value="small_mammal">Small Mammal</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    {/* Name — always rendered */}
                    <div>
                      <label htmlFor={`pet-${index}-name`} className={labelClass}>Name <RequiredMark /></label>
                      <Input
                        id={`pet-${index}-name`}
                        type="text"
                        data-testid={`pet-${index}-name`}
                        value={animal.name}
                        onChange={e => updateAnimal(index, { name: e.target.value })}
                        className="min-h-[44px]"
                        placeholder="e.g. Buddy"
                        required
                        aria-required="true"
                        aria-describedby={errorId}
                        aria-invalid={animalHasError && !animal.name.trim() ? true : undefined}
                      />
                    </div>

                    {/* Color — always rendered */}
                    <div>
                      <label htmlFor={`pet-${index}-color`} className={labelClass}>Color / Markings</label>
                      <Input
                        id={`pet-${index}-color`}
                        type="text"
                        data-testid={`pet-${index}-color`}
                        value={animal.color ?? ''}
                        onChange={e => updateAnimal(index, { color: e.target.value })}
                        className="min-h-[44px]"
                        placeholder="e.g. Golden"
                      />
                    </div>

                    {/* Dog / Cat conditional fields */}
                    {(animal.type === 'dog' || animal.type === 'cat') && (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label htmlFor={`pet-${index}-breed`} className={labelClass}>Breed <RequiredMark /></label>
                            <Input
                              id={`pet-${index}-breed`}
                              type="text"
                              data-testid={`pet-${index}-breed`}
                              value={animal.breed ?? ''}
                              onChange={e => updateAnimal(index, { breed: e.target.value })}
                              className="min-h-[44px]"
                              placeholder="e.g. Golden Retriever"
                              required
                              aria-required="true"
                              aria-describedby={errorId}
                              aria-invalid={animalHasError && !animal.breed?.trim() ? true : undefined}
                            />
                          </div>
                          <div>
                            <label htmlFor={`pet-${index}-weight`} className={labelClass}>Weight (lbs) <RequiredMark /></label>
                            <Input
                              id={`pet-${index}-weight`}
                              type="number"
                              data-testid={`pet-${index}-weight`}
                              value={animal.weight ?? ''}
                              min={0}
                              step={1}
                              onChange={e => updateAnimal(index, { weight: e.target.value !== '' ? Number(e.target.value) : undefined })}
                              className="min-h-[44px]"
                              placeholder="e.g. 45"
                              required
                              aria-required="true"
                              aria-describedby={errorId}
                              aria-invalid={animalHasError && (!animal.weight || animal.weight <= 0) ? true : undefined}
                            />
                          </div>
                        </div>
                        <div>
                          <label htmlFor={`pet-${index}-sex`} className={labelClass}>Sex <RequiredMark /></label>
                          <select
                            id={`pet-${index}-sex`}
                            data-testid={`pet-${index}-sex`}
                            value={animal.sex ?? ''}
                            onChange={e => updateAnimal(index, { sex: e.target.value as PetDetail['sex'] })}
                            className={nativeSelectClass}
                            required
                            aria-required="true"
                            aria-invalid={animalHasError && !animal.sex ? true : undefined}
                            aria-describedby={errorId}
                          >
                            <option value="">Select sex</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                            <option value="unknown">Unknown</option>
                          </select>
                        </div>
                        <div className="flex flex-wrap gap-x-6">
                          <label className="flex items-center gap-2 min-h-[44px] cursor-pointer text-sm text-foreground">
                            <Switch
                              checked={animal.spayedNeutered ?? false}
                              onCheckedChange={checked => updateAnimal(index, { spayedNeutered: checked })}
                              data-testid={`pet-${index}-spayed-neutered`}
                            />
                            Spayed / Neutered
                          </label>
                          <label className="flex items-center gap-2 min-h-[44px] cursor-pointer text-sm text-foreground">
                            <Switch
                              checked={animal.vaccinationsCurrent ?? false}
                              onCheckedChange={checked => updateAnimal(index, { vaccinationsCurrent: checked })}
                              data-testid={`pet-${index}-vaccinations`}
                            />
                            Vaccinations Current
                          </label>
                        </div>
                      </>
                    )}

                    {/* Bird conditional fields */}
                    {animal.type === 'bird' && (
                      <>
                        <div>
                          <label htmlFor={`pet-${index}-species`} className={labelClass}>Species <RequiredMark /></label>
                          <Input
                            id={`pet-${index}-species`}
                            type="text"
                            data-testid={`pet-${index}-species`}
                            value={animal.species ?? ''}
                            onChange={e => updateAnimal(index, { species: e.target.value })}
                            className="min-h-[44px]"
                            placeholder="e.g. Parrot"
                            required
                            aria-required="true"
                            aria-describedby={errorId}
                            aria-invalid={animalHasError && !animal.species?.trim() ? true : undefined}
                          />
                        </div>
                        <div>
                          <label htmlFor={`pet-${index}-cage-description`} className={labelClass}>Cage / housing <RequiredMark /></label>
                          <Input
                            id={`pet-${index}-cage-description`}
                            type="text"
                            data-testid={`pet-${index}-cage-description`}
                            value={animal.cageDescription ?? ''}
                            onChange={e => updateAnimal(index, { cageDescription: e.target.value })}
                            className="min-h-[44px]"
                            placeholder="e.g. Large cage, kept in living room"
                            required
                            aria-required="true"
                            aria-describedby={errorId}
                            aria-invalid={animalHasError && !animal.cageDescription?.trim() ? true : undefined}
                          />
                        </div>
                      </>
                    )}

                    {/* Reptile conditional fields */}
                    {animal.type === 'reptile' && (
                      <>
                        <div>
                          <label htmlFor={`pet-${index}-species`} className={labelClass}>Species <RequiredMark /></label>
                          <Input
                            id={`pet-${index}-species`}
                            type="text"
                            data-testid={`pet-${index}-species`}
                            value={animal.species ?? ''}
                            onChange={e => updateAnimal(index, { species: e.target.value })}
                            className="min-h-[44px]"
                            placeholder="e.g. Ball Python"
                            required
                            aria-required="true"
                            aria-describedby={errorId}
                            aria-invalid={animalHasError && !animal.species?.trim() ? true : undefined}
                          />
                        </div>
                        <div>
                          <label htmlFor={`pet-${index}-enclosure-description`} className={labelClass}>Enclosure / housing <RequiredMark /></label>
                          <Input
                            id={`pet-${index}-enclosure-description`}
                            type="text"
                            data-testid={`pet-${index}-enclosure-description`}
                            value={animal.enclosureDescription ?? ''}
                            onChange={e => updateAnimal(index, { enclosureDescription: e.target.value })}
                            className="min-h-[44px]"
                            placeholder="e.g. 40-gallon terrarium, locked"
                            required
                            aria-required="true"
                            aria-describedby={errorId}
                            aria-invalid={animalHasError && !animal.enclosureDescription?.trim() ? true : undefined}
                          />
                        </div>
                        <label className="flex items-center gap-2 min-h-[44px] cursor-pointer text-sm text-foreground">
                            <Switch
                              checked={animal.venomous ?? false}
                              onCheckedChange={checked => updateAnimal(index, { venomous: checked })}
                              data-testid={`pet-${index}-venomous`} className="data-[state=checked]:bg-destructive"
                            />
                            Venomous
                          </label>
                      </>
                    )}

                    {/* Fish conditional fields */}
                    {animal.type === 'fish' && (
                      <div>
                        <label htmlFor={`pet-${index}-tank-size`} className={labelClass}>Tank size (gallons) <RequiredMark /></label>
                        <Input
                          id={`pet-${index}-tank-size`}
                          type="number"
                          data-testid={`pet-${index}-tank-size`}
                          value={animal.tankSizeGallons ?? ''}
                          min={1}
                          step={1}
                          onChange={e => updateAnimal(index, { tankSizeGallons: e.target.value !== '' ? Number(e.target.value) : undefined })}
                          className="min-h-[44px]"
                          placeholder="e.g. 20"
                          required
                          aria-required="true"
                          aria-describedby={errorId}
                          aria-invalid={animalHasError && (!animal.tankSizeGallons || animal.tankSizeGallons <= 0) ? true : undefined}
                        />
                      </div>
                    )}

                    {/* Small mammal conditional fields */}
                    {animal.type === 'small_mammal' && (
                      <div>
                        <label htmlFor={`pet-${index}-species`} className={labelClass}>Species <RequiredMark /></label>
                        <Input
                          id={`pet-${index}-species`}
                          type="text"
                          data-testid={`pet-${index}-species`}
                          value={animal.species ?? ''}
                          onChange={e => updateAnimal(index, { species: e.target.value })}
                          className="min-h-[44px]"
                          placeholder="e.g. Guinea Pig"
                          required
                          aria-required="true"
                          aria-describedby={errorId}
                          aria-invalid={animalHasError && !animal.species?.trim() ? true : undefined}
                        />
                      </div>
                    )}
                    {animalHasError && (
                      <p
                        id={errorId}
                        data-testid={`pet-${index}-error`}
                        className="text-xs text-warning bg-warning/10 border border-warning/20 rounded px-3 py-2"
                      >
                        Please complete all required fields for this pet.
                      </p>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        ))}

        {animals.some(petHasError) && (
          <p role="alert" data-testid="pet-list-error" className="text-xs text-warning bg-warning/10 border border-warning/20 rounded px-3 py-2 mb-3">
            Please complete all required fields for each pet.
          </p>
        )}

        {animals.length === 0 && (
          <p className="text-xs text-warning bg-warning/10 border border-warning/20 rounded px-3 py-2 mb-3">
            At least one pet must be documented when pets are allowed.
          </p>
        )}

        <Button
          type="button"
          variant="outline"
          data-testid="add-pet-button"
          onClick={() => addAnimal({ ...BLANK_ANIMAL })}
          className="w-full min-h-[44px] border-dashed text-primary border-primary/40 hover:bg-primary/5"
        >
          + Add Another Pet
        </Button>
      </div>
  );
}
